/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type ComponentRef, type OnDestroy, type OnInit, Component, HostListener, TemplateRef, ViewChild } from '@angular/core';
import { type ButtonComponent, type Polling, type PromptComponent, type PromptConfigFull, CoreEventServiceKeyE, DialogTypeE, EventService, PermissionService, PollingService, PromptService, TimeService } from '@dm/core';
import { UNPLACEHOLDER } from 'app/common/constants';
import { type RemoteAssistanceTask } from 'app/common/types/mo';
import { SWITCH } from 'app/config';

import { ONE_MINUTE, REMOTE_ASSISTANCE_DIALOG_HEIGHT, REMOTE_ASSISTANCE_DIALOG_WIDTH, REMOTE_ASSISTANCE_POLLING_TIME, RemoteAssistanceTaskParamE, RemoteAssistanceTaskStatusE } from './remote-assistance.const';
import { RemoteAssistanceService } from './remote-assistance.service';
import { RemoteAssistanceStrService } from './remote-assistance-str.service';

@Component({
    selector: 'app-remote-assistance',
    templateUrl: './remote-assistance.component.html',
    styleUrls: ['./remote-assistance.component.scss'],
})
export class RemoteAssistanceComponent implements OnInit, OnDestroy {
    /**
     * 申请DME IQ远程协助弹窗底部按钮
     */
    @ViewChild('applicationFooterBtn') applicationFooterBtn: TemplateRef<ButtonComponent>;
    /**
     * 结束DME IQ远程协助弹窗底部按钮
     */
    @ViewChild('endFooterBtn') endFooterBtn: TemplateRef<ButtonComponent>;
    /**
     * 是否显示上方剩余时间
     */
    isShow = false;
    /**
     * 申请/结束协助的弹窗
     */
    promptComp: ComponentRef<PromptComponent>;
    /**
     * 本地的任务状态
     */
    localTaskStatus: RemoteAssistanceTaskStatusE;
    /**
     * 剩余时间
     */
    remainingTime: string;
    /**
     * 轮询
     */
    polling$: Polling;
    /**
     * 资料
     */
    i18n = this.str.i18n;
    /**
     * 控制“结束”按钮
     */
    hasCallhomeW: boolean;
    /**
     * 是否显示“结束”按钮的Loading
     */
    btnLoading = false;
    /**
     * 远程协助-标题
     */
    remoteAssistanceContentTitle = this.i18n.assistingTitle;
    /**
     * 显示持续时间
     */
    showLeftTime = false;
    /**
     * 常量
     */
    RemoteAssistanceTaskParamE = RemoteAssistanceTaskParamE;
    SWITCH = SWITCH;

    constructor(
        private str: RemoteAssistanceStrService,
        private service: RemoteAssistanceService,
        private pollingService: PollingService,
        private timeService: TimeService,
        private promptService: PromptService,
        private eventService: EventService,
        private permissionService: PermissionService,
    ) { }

    /**
     * 检测header上方提示信息的变更，可能导致高度变化，需适配部分场景的显示优化
     * 场景包括：侧滑框（极简模式自定义侧滑框）、气泡toast、高级搜索全局模式、登陆信息等场景的top值
     * 窗口尺寸变化可能导致提示信息自动换行，高度发生变化，也需要重新适配
     */
    @HostListener('window:resize')
    adjustChangeOfTipsOverHeader(): void {
        // 异步执行，确保元素页面的呈现已经变化
        setTimeout(() => {
            this.eventService.emit(CoreEventServiceKeyE.CHECK_TIPS_OVER_HEADER);
        });
    }

    ngOnInit(): void {
        this.getData();
        this.hasCallhomeW = this.permissionService.hasPermission('call_home_W');
    }

    getData(): void {
        this.polling$ = this.pollingService.add(this.service.getRemoteStatusRest(), REMOTE_ASSISTANCE_POLLING_TIME);
        this.polling$.subscribe(
            ({ data }: { data: RemoteAssistanceTask }) => {
                // 任务状态变化，需要将弹窗关闭
                if (this.promptComp && this.localTaskStatus !== data.taskStatus) {
                    this.clearPrompt();
                }

                this.localTaskStatus = data.taskStatus;
                this.showLeftTime = data.taskStatus === RemoteAssistanceTaskStatusE.RUNNING;

                switch (data.taskStatus) {
                    //正在建立远程协助
                    case RemoteAssistanceTaskStatusE.CONNECTING:
                        this.showChange(true);
                        this.remoteAssistanceContentTitle = this.i18n.establishingAssistingTitle;
                        break;
                    //远程任务运行中
                    case RemoteAssistanceTaskStatusE.RUNNING:
                        this.showChange(true);
                        this.updateRemainingTime(data);
                        this.remoteAssistanceContentTitle = this.i18n.assistingTitle;
                        break;
                    //正在结束远程协助
                    case RemoteAssistanceTaskStatusE.TERMINATING:
                        this.showChange(true);
                        this.remoteAssistanceContentTitle = this.i18n.endAssistingTitle;
                        break;
                    //正在重连远程协助
                    case RemoteAssistanceTaskStatusE.RECONNECTION:
                        this.showChange(true);
                        this.remoteAssistanceContentTitle = this.i18n.reconnectionAssistingTitle;
                        break;
                    //请求等待任务
                    case RemoteAssistanceTaskStatusE.WAITING:
                        this.showChange(false);

                        if (!this.promptComp && this.hasCallhomeW) {
                            this.showRemoteAssistance();
                        }

                        break;
                    //无任务
                    default:
                        this.showChange(false);
                        break;
                }
            },
            () => {
                this.showChange(false);
            },
        );
    }

    /**
     * 更新协助剩余时间
     */
    updateRemainingTime(taskInfo: RemoteAssistanceTask): void {
        // 小时转换成秒
        const timeout = Number(taskInfo.remotePolicyTimeout) * 3600;
        const time = timeout - (this.timeService.currTime - Number(taskInfo.remoteAssistanceBeginTime));
        if (isNaN(time)) {
            this.remainingTime = this.i18n.remainingTime + this.i18n.minutes(UNPLACEHOLDER);
            return;
        }

        if (time <= 0) {
            this.showChange(false);
            return;
        }

        // 小于60秒，展示为“< 1分钟”
        if (time < 60) {
            this.remainingTime = `< ${this.i18n.minutes(ONE_MINUTE)}`;
            return;
        }

        // 最终展示为“分钟”
        this.remainingTime = this.i18n.remainingTime + this.i18n.minutes(Math.floor(time / 60));
    }

    /**
     * 远程协助申请框
     */
    showRemoteAssistance(): void {
        this.promptComp = this.promptService.normal({
            title: this.i18n.requestTitle,
            content: this.i18n.requestContent,
            titleIcon: '#app-icon-simple-dme-iq',
            width: SWITCH.IS_SIMPLE_MODE ? REMOTE_ASSISTANCE_DIALOG_WIDTH : undefined,
            height: SWITCH.IS_SIMPLE_MODE ? REMOTE_ASSISTANCE_DIALOG_HEIGHT : undefined,
            dialogType: SWITCH.IS_SIMPLE_MODE ? DialogTypeE.SIMPLE_MODE : DialogTypeE.DEFAULT,
            footer: this.applicationFooterBtn,
            onCancel: () => {
                this.handleRemoteRequest(RemoteAssistanceTaskParamE.REJECT);
            },
        });
    }

    /**
     * 结束远程协助框
     */
    endRemoteAssistance(): void {
        this.promptComp = this.promptService.normal({
            title: this.i18n.endRequestTitle,
            content: this.i18n.endRequestContent,
            titleIcon: '#app-icon-simple-dme-iq',
            width: SWITCH.IS_SIMPLE_MODE ? REMOTE_ASSISTANCE_DIALOG_WIDTH : undefined,
            height: SWITCH.IS_SIMPLE_MODE ? REMOTE_ASSISTANCE_DIALOG_HEIGHT : undefined,
            dialogType: SWITCH.IS_SIMPLE_MODE ? DialogTypeE.SIMPLE_MODE : DialogTypeE.DEFAULT,
            footer: this.endFooterBtn,
            onCancel: () => {
                this.handleRemoteRequest();
            },
        });
    }

    /**
     * 处理远程协助操作
     * @param operate 接受、拒绝、终止操作
     */
    handleRemoteRequest(operate?: RemoteAssistanceTaskParamE): void {
        if (!operate) {
            this.clearPrompt();
            return;
        }

        if (operate !== RemoteAssistanceTaskParamE.ACCEPT) {
            this.sendRequest(operate);
            return;
        }

        // 同意远程协助自动生成远程协助用户提示
        const prompts: PromptConfigFull[] = [{
            id: 'remote-assistance-create-user-tip',
            opType: 'warning',
            title: this.i18n.requestTitle,
            content: this.i18n.agreeRemoteAssistanceTip,
        }];

        this.promptService.multi(prompts, () => {
            this.promptService.close();
            this.sendRequest(operate);
        }, () => {
            this.promptService.close();
        });
    }

    /**
     * 下发远程协助请求
     * @param operate 接受、拒绝、终止操作
     */
    sendRequest(operate?: RemoteAssistanceTaskParamE): void {
        const name = this.str.restName[operate];
        if (operate === RemoteAssistanceTaskParamE.TERMINATE) {
            this.btnLoading = true;
        }

        this.service.handleRemoteRequest(operate, { name }).subscribe(
            () => {
                if (operate === RemoteAssistanceTaskParamE.TERMINATE) {
                    this.btnLoading = false;
                }

                // 执行“同意”或“拒绝”后，立即查询一次
                if ([RemoteAssistanceTaskParamE.ACCEPT, RemoteAssistanceTaskParamE.TERMINATE].includes(operate)) {
                    this.polling$?.immediate();
                }
            },
            () => {
                if (operate === RemoteAssistanceTaskParamE.TERMINATE) {
                    this.btnLoading = false;
                }
            },
        );
        this.clearPrompt();
    }

    /**
     * 更新远程协助栏显示状态，更新header及上方提示信息高度
     * @param isShow 远程协助栏是否显示
     */
    showChange(isShow: boolean): void {
        this.isShow = isShow;
        this.adjustChangeOfTipsOverHeader();
    }

    /**
     * 清理弹窗实例
     */
    clearPrompt(): void {
        this.promptService.close();
        this.promptComp = null;
    }

    ngOnDestroy(): void {
        this.pollingService.remove(this.polling$);
        this.polling$ = null;
        this.promptComp = null;
    }
}
